import './ChangePassword.less';
import React from 'react';
import AppLayout from '../../containers/AppLayout/AppLayout';
import { Input, Button, Form, Icon, AppLocaleProvider } from 'sd-ui-components';
import { observer, inject } from 'mobx-react';
import PageLoadLogger from '../../utils/PageLoadLogger';
import Notifications from '../../components/Notifications/Notifications';

const FormItem = Form.Item;
const { FormattedMessage, injectIntl, intlShape } = AppLocaleProvider;

function hasErrors(fieldsError) {
  return Object.keys(fieldsError).some(field => fieldsError[field]);
}

@inject('ConfigStore')
@inject('AuthStore')
@inject('NotificationsStore')
@observer
class ChangePassword extends React.Component {
  pageLoadLogger = new PageLoadLogger('Change password');

  constructor() {
    super();

    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleClose = this.handleClose.bind(this);
  }

  renderSave = () => {
    const form = this.props.form;
    const inputsHaveErrors = hasErrors(form.getFieldsError());
    const newPassword = form.getFieldValue('newPassword');
    const confirmNewPassword = form.getFieldValue('confirmNewPassword');

    const doPasswordsMatch = newPassword === confirmNewPassword;

    const allowSave = !inputsHaveErrors && doPasswordsMatch;
    const passwordMatchStyle =
      !inputsHaveErrors && !doPasswordsMatch
        ? { visibility: 'visible', color: '#f5222d' }
        : { visibility: 'hidden' };

    return (
      <span>
        <div style={passwordMatchStyle}>
          <FormattedMessage
            id="App.ChangePassword.Errors.NewPasswordsDontMatch"
            defaultMessage="New passwords don't match"
          />
        </div>

        <FormItem>
          <Button
            type="primary"
            htmlType="submit"
            size="large"
            className="changepassword-button"
            disabled={!allowSave}
          >
            <FormattedMessage
              defaultMessage="Save new password"
              id="App.ChangePassword.SaveNewPassword"
            />
          </Button>
        </FormItem>
      </span>
    );
  };

  handleSubmit(e) {
    e.preventDefault();
    this.props.form.validateFields(err => {
      if (!err) {
        this.password();
      }
    });
  }

  handleClose = e => {
    this.props.history.goBack();
  };

  componentDidMount() {
    this.props.form.validateFields();
    this.pageLoadLogger.capture();
  }

  password = () => {
    this.props.AuthStore.passwordInfo.userName = this.props.AuthStore.userInfo.userName;
    this.props.AuthStore.passwordInfo.company = this.props.AuthStore.userInfo.companyHash;
    this.props.AuthStore.passwordInfo.oldPassword = this.props.form.getFieldValue(
      'oldPassword'
    );
    this.props.AuthStore.passwordInfo.newPassword = this.props.form.getFieldValue(
      'newPassword'
    );
    this.props.AuthStore.passwordInfo.confirmNewPassword = this.props.form.getFieldValue(
      'confirmNewPassword'
    );

    this.props.AuthStore.password().then(value => {
      if (value === true) {
        document.getElementById('changePasswordInfo').style.display = 'none';
        document.getElementById('changePasswordForm').style.display = 'none';
      }
    });
  };

  componentWillUnmount = () => {
    this.props.NotificationsStore.clearNotifications();
  };

  getPasswordRules = (requiredMessage, requiredMessageId) => {
    return {
      rules: [
        {
          required: true,
          message: (
            <FormattedMessage
              defaultMessage={requiredMessage}
              id={requiredMessageId}
            />
          )
        },
        {
          min: this.props.ConfigStore.config.minPasswordLength,
          message: (
            <FormattedMessage
              defaultMessage="Must be between {minLength} and {maxLength} characters long"
              id="App.ChangePassword.Errors.PasswordLength"
              values={{
                minLength: this.props.ConfigStore.config.minPasswordLength,
                maxLength: this.props.ConfigStore.config.maxPasswordLength
              }}
            />
          )
        },
        {
          pattern: /^(?=.*[0-9])(?=.*[a-zA-Z])/,
          message: (
            <FormattedMessage
              defaultMessage="Must contain at least one letter and one number"
              id="App.ChangePassword.Errors.OneLetterAndOneNumber"
            />
          )
        },
        {
          pattern: /^[a-zA-Z0-9$#@^*()&%!]*$/,
          message: (
            <FormattedMessage
              defaultMessage="Only the following special characters are supported: $ # @ ! % ^ & * ( )"
              id="App.ChangePassword.Errors.AllowedCharacters"
            />
          )
        }
      ]
    };
  };

  render() {
    const {
      getFieldDecorator,
      getFieldError,
      isFieldTouched
    } = this.props.form;

    const oldPasswordError =
      isFieldTouched('oldPassword') && getFieldError('oldPassword');
    const newPasswordError =
      isFieldTouched('newPassword') && getFieldError('newPassword');
    const confirmNewPasswordError =
      isFieldTouched('confirmNewPassword') &&
      getFieldError('confirmNewPassword');

    return (
      <AppLayout>
        <div>
          <div>
            <h1>
              <FormattedMessage
                id="App.ChangePassword.ChangePassword"
                defaultMessage="Change password"
              />
              <span>
                <Icon
                  icon="cross2"
                  onClick={this.handleClose}
                  className="changepassword-close-icon"
                  color="#222222"
                  size={30}
                />
              </span>
            </h1>
          </div>
          <div id="changePasswordInfo" className="changepassword-infotext">
            <p>
              <span>
                <FormattedMessage
                  defaultMessage="For security purposes, your new password must:"
                  id="App.ChangePassword.Info.Description"
                />
              </span>
            </p>
            <p>
              <span>
                &bull;{' '}
                <FormattedMessage
                  defaultMessage={`Be between ${this.props.ConfigStore.config.minPasswordLength} and ${this.props.ConfigStore.config.maxPasswordLength} characters long`}
                  id="App.ChangePassword.Info.MinimumLength"
                  values={{
                    minLength: this.props.ConfigStore.config.minPasswordLength,
                    maxLength: this.props.ConfigStore.config.maxPasswordLength
                  }}
                />
              </span>
            </p>
            <p>
              <span>
                &bull;{' '}
                <FormattedMessage
                  defaultMessage="Contain at least one number"
                  id="App.ChangePassword.Info.OneNumber"
                />
              </span>
            </p>
            <p>
              <span>
                &bull;{' '}
                <FormattedMessage
                  defaultMessage="Contain at least one letter"
                  id="App.ChangePassword.Info.OneLetter"
                />
              </span>
            </p>
          </div>
        </div>
        <div>
          <Notifications />
        </div>

        <Form id="changePasswordForm" onSubmit={this.handleSubmit}>
          <FormItem
            validateStatus={oldPasswordError ? 'error' : ''}
            help={oldPasswordError || ''}
          >
            {getFieldDecorator('oldPassword', {
              rules: [
                {
                  required: true,
                  message: (
                    <FormattedMessage
                      defaultMessage="Current password is required"
                      id="App.ChangePassword.Errors.CurrentPasswordIsRequired"
                    />
                  )
                }
              ]
            })(
              <Input
                type="password"
                size="large"
                className="changepassword-oldpassword"
                placeholder={this.props.intl.formatMessage({
                  id: 'App.ChangePassword.CurrentPassword',
                  defaultMessage: 'Current password'
                })}
                required
              />
            )}
          </FormItem>

          <FormItem
            validateStatus={newPasswordError ? 'error' : ''}
            help={
              (newPasswordError &&
                newPasswordError.length > 0 &&
                newPasswordError[0]) ||
              ''
            }
          >
            {getFieldDecorator(
              'newPassword',
              this.getPasswordRules(
                'New password is required',
                'App.ChangePassword.Errors.NewPasswordIsRequired'
              )
            )(
              <Input
                type="password"
                size="large"
                maxLength={this.props.ConfigStore.config.maxPasswordLength}
                className="changepassword-newpassword"
                placeholder={this.props.intl.formatMessage({
                  id: 'App.ChangePassword.NewPassword',
                  defaultMessage: 'New password'
                })}
                required
              />
            )}
          </FormItem>

          <FormItem
            validateStatus={confirmNewPasswordError ? 'error' : ''}
            help={
              (confirmNewPasswordError &&
                confirmNewPasswordError.length > 0 &&
                confirmNewPasswordError[0]) ||
              ''
            }
          >
            {getFieldDecorator(
              'confirmNewPassword',
              this.getPasswordRules(
                'Re-entering new password is required',
                'App.ChangePassword.Errors.ConfirmNewPasswordIsRequired'
              )
            )(
              <Input
                type="password"
                size="large"
                maxLength={this.props.ConfigStore.config.maxPasswordLength}
                placeholder={this.props.intl.formatMessage({
                  id: 'App.ChangePassword.ConfirmNewPassword',
                  defaultMessage: 'Confirm new password'
                })}
                required
              />
            )}
          </FormItem>

          {this.renderSave()}
        </Form>
      </AppLayout>
    );
  }
}

ChangePassword.propTypes = {
  intl: intlShape.isRequired
};

const WrappedForm = Form.create()(injectIntl(ChangePassword));
export default WrappedForm;



// WEBPACK FOOTER //
// ./src/containers/ChangePassword/ChangePassword.jsx